import React, { Suspense, useEffect } from 'react';
import { connect } from 'react-redux';
import { Route, Routes } from 'react-router-dom';

import CookieConsentBanner from '../components/cookie-consent-banner';
import Footer from '../components/footer';
import Messenger from '../components/messenger';
import RequestFailedBanner from '../components/request-failed-banner';
import SearchResult from '../components/search-result';
import Topbar from '../components/topbar';
import DeliveryContainer from './delivery-container';
import GiftRegistryPublicContainer from './gift-registry-public';
import HomeContainer from './home-container';
import LayoutContent from './layout-content';
import LayoutSidebarContent from './layout-sidebar-content';
import OrderContainer from './order-container';
import PrivacyPolicyContainer from './privacy-policy-container';
import ProductContainer from './product-container';
import ProductsContainer from './products-container';
import ReviewContainer from './review-container';
import ReviewsContainer from './reviews-container';
import SearchContainer from './search-container';
import TakeBackContainer from './take-back-container';
import TermsAndConditionsContainer from './terms-and-conditions-container';

const BasketContainer = React.lazy(() => import('./basket-container'));
const MarketingPreferenceContainer = React.lazy(
  () => import('./marketing-preference'),
);
const SignInContainer = React.lazy(() => import('./account/sign-in'));
const WithdrawalContainer = React.lazy(() => import('./withdrawal'));
const AccountLayoutContainer = React.lazy(() => import('./account/layout'));
const AccountOverviewContainer = React.lazy(() => import('./account/overview'));
const AccountOrdersContainer = React.lazy(() => import('./account/orders'));
const AccountOrderDetailContainer = React.lazy(
  () => import('./account/order-detail'),
);
const AccountAddressesContainer = React.lazy(
  () => import('./account/addresses'),
);
const AccountCouponsContainer = React.lazy(() => import('./account/coupons'));
const AccountReferralContainer = React.lazy(() => import('./account/referral'));
const AccountGiftRegistriesContainer = React.lazy(
  () => import('./account/gift-registries'),
);
const AccountGiftRegistryDetailContainer = React.lazy(
  () => import('./account/gift-registry-detail'),
);
const AccountSettingsContainer = React.lazy(() => import('./account/settings'));

function Layout(): React.JSX.Element {
  // Signals to e2e tests that hydration has committed and event handlers
  // are attached — interacting with the SSR-painted DOM before this point
  // silently no-ops (native events with no React listener yet).
  useEffect(() => {
    document.body.dataset.hydrated = 'true';
  }, []);

  return (
    <div className="container">
      <a href="#main-content" className="skip-nav">
        Skip to content
      </a>

      <Topbar />

      <SearchResult />

      <main id="main-content">
        <Routes>
          <Route
            index
            path="/:language"
            element={<LayoutSidebarContent element={<HomeContainer />} />}
          />
          <Route
            index
            path="/:language/home"
            element={<LayoutSidebarContent element={<HomeContainer />} />}
          />
          <Route
            path="/:language/basket"
            element={
              <Suspense fallback={null}>
                <LayoutSidebarContent element={<BasketContainer />} />
              </Suspense>
            }
          />
          <Route
            path="/:language/delivery"
            element={<LayoutSidebarContent element={<DeliveryContainer />} />}
          />
          <Route
            path="/:language/order/:orderUUID"
            element={<LayoutSidebarContent element={<OrderContainer />} />}
          />
          <Route
            path="/:language/product/:productUUID"
            element={<LayoutSidebarContent element={<ProductContainer />} />}
          />
          <Route
            path="/:language/p/:slug"
            element={<LayoutSidebarContent element={<ProductContainer />} />}
          />
          <Route
            path="/:language/category/:categoryUUID/:pageUrl"
            element={<LayoutSidebarContent element={<ProductsContainer />} />}
          />
          <Route
            path="/:language/category/:categoryUUID"
            element={<LayoutSidebarContent element={<ProductsContainer />} />}
          />
          <Route
            path="/:language/c/:slug/:pageUrl"
            element={<LayoutSidebarContent element={<ProductsContainer />} />}
          />
          <Route
            path="/:language/c/:slug"
            element={<LayoutSidebarContent element={<ProductsContainer />} />}
          />
          <Route
            path="/:language/search"
            element={<LayoutSidebarContent element={<SearchContainer />} />}
          />
          <Route
            path="/:language/terms-and-conditions"
            element={
              <LayoutSidebarContent element={<TermsAndConditionsContainer />} />
            }
          />
          <Route
            path="/:language/terms-and-conditions/:versionCode"
            element={
              <LayoutSidebarContent element={<TermsAndConditionsContainer />} />
            }
          />
          <Route
            path="/:language/privacy-policy"
            element={
              <LayoutSidebarContent element={<PrivacyPolicyContainer />} />
            }
          />
          <Route
            path="/:language/privacy-policy/:versionCode"
            element={
              <LayoutSidebarContent element={<PrivacyPolicyContainer />} />
            }
          />
          <Route
            path="/:language/take-back"
            element={<LayoutSidebarContent element={<TakeBackContainer />} />}
          />
          <Route
            path="/:language/review/:reviewUUID"
            element={<LayoutContent element={<ReviewContainer />} />}
          />
          <Route
            path="/:language/reviews/"
            element={<LayoutSidebarContent element={<ReviewsContainer />} />}
          />
          <Route
            path="/:language/gift-registry/:shareToken"
            element={
              <LayoutContent element={<GiftRegistryPublicContainer />} />
            }
          />
          <Route
            path="/:language/marketing-preferences/:token"
            element={
              <Suspense fallback={null}>
                <LayoutContent element={<MarketingPreferenceContainer />} />
              </Suspense>
            }
          />
          <Route
            path="/:language/withdrawal"
            element={
              <Suspense fallback={null}>
                <LayoutContent element={<WithdrawalContainer />} />
              </Suspense>
            }
          />
          <Route
            path="/:language/withdrawal/:orderUUID"
            element={
              <Suspense fallback={null}>
                <LayoutContent element={<WithdrawalContainer />} />
              </Suspense>
            }
          />
          <Route
            path="/:language/account/login"
            element={
              <Suspense fallback={null}>
                <LayoutContent element={<SignInContainer />} />
              </Suspense>
            }
          />
          <Route
            path="/:language/account"
            element={
              <Suspense fallback={null}>
                <AccountLayoutContainer />
              </Suspense>
            }
          >
            <Route index element={<AccountOverviewContainer />} />
            <Route
              path="orders/:orderUUID"
              element={<AccountOrderDetailContainer />}
            />
            <Route path="orders" element={<AccountOrdersContainer />} />
            <Route path="addresses" element={<AccountAddressesContainer />} />
            <Route path="coupons" element={<AccountCouponsContainer />} />
            <Route path="referral" element={<AccountReferralContainer />} />
            <Route
              path="gift-registries"
              element={<AccountGiftRegistriesContainer />}
            />
            <Route
              path="gift-registries/:registryUUID"
              element={<AccountGiftRegistryDetailContainer />}
            />
            <Route path="settings" element={<AccountSettingsContainer />} />
          </Route>
        </Routes>
      </main>

      <Messenger />

      <Footer />

      <CookieConsentBanner />

      <RequestFailedBanner />
    </div>
  );
}

const mapStateToProps = ({ Config }) => ({
  Config,
});

export default connect(mapStateToProps)(Layout);
